import React, { useState, useEffect } from 'react';
import { authService, supabase } from '../../services/supabase';

const ManagerLogin = ({ onSuccess, onSwitchToRegister, onSwitchToWaiter }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  useEffect(() => {
    document.title = "Owner / Manager Login - Menuron";
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!authService.isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      const { data, error } = await authService.loginManager(formData.email, formData.password);

      if (error) {
        setErrors({ general: error.message });
        return;
      }

      onSuccess(data);

    } catch (error) {
      setErrors({ general: 'Login failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim() || !authService.isValidEmail(forgotEmail)) {
      setForgotMessage('error:Please enter a valid email address.');
      return;
    }

    setForgotLoading(true);
    setForgotMessage('');

    try {
      // Ensure the owner has a Supabase Auth account (creates one if needed for migration)
      const { ensureOwnerInSupabaseAuth } = await import('../../services/authMigration');
      const redirectTo = `${window.location.origin}/reset-password`;
      await ensureOwnerInSupabaseAuth(forgotEmail, redirectTo);

      // Send password reset email via Supabase Auth
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.toLowerCase(), {
        redirectTo,
      });

      if (error) throw error;

      setForgotMessage('success:Password reset link sent! Check your email inbox.');
    } catch (error) {
      console.error('Forgot password error:', error);
      // Always show success message to prevent email enumeration
      setForgotMessage('success:If an account exists with this email, a reset link has been sent.');
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '40px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        width: '100%',
        maxWidth: '400px',
        border: '1px solid rgba(255, 68, 68, 0.1)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="https://i.ibb.co/hF3Q8YrX/menuron-high-resolution-logo.png"
            alt="Menuron"
            style={{ height: '140px', marginBottom: '20px', filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' }}
          />
          <h1 style={{
            margin: '0 0 10px 0',
            fontSize: '1.8rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Owner / Manager Login
          </h1>
          <p style={{ margin: '0', color: '#666', fontSize: '1rem' }}>
            Sign in as a restaurant owner or manager
          </p>
        </div>

        {/* Error Message */}
        {errors.general && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '20px',
            color: '#dc2626',
            fontSize: '0.9rem'
          }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <input
              type="email"
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
              style={{
                width: '100%',
                padding: '15px 18px',
                border: errors.email ? '2px solid #dc2626' : '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF6B00'}
              onBlur={(e) => e.target.style.borderColor = errors.email ? '#dc2626' : '#e5e7eb'}
            />
            {errors.email && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {errors.email}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '15px 45px 15px 18px',
                  border: errors.password ? '2px solid #dc2626' : '2px solid #e5e7eb',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  outline: 'none',
                  transition: 'border-color 0.3s ease',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#FF6B00'}
                onBlur={(e) => e.target.style.borderColor = errors.password ? '#dc2626' : '#e5e7eb'}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex', alignItems: 'center' }}>
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
            {errors.password && (
              <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '5px' }}>
                {errors.password}
              </div>
            )}
          </div>

          {/* Forgot Password Link */}
          <div style={{ textAlign: 'right', marginBottom: '20px' }}>
            <button
              type="button"
              onClick={() => {
                setForgotEmail(formData.email);
                setShowForgotPassword(true);
                setForgotMessage('');
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#FF6B00',
                fontSize: '0.9rem',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              background: loading ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '15px',
              fontSize: '1.1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 6px 20px rgba(255, 68, 68, 0.3)',
              transition: 'all 0.3s ease',
              marginBottom: '20px'
            }}
          >
            {loading ? 'Logging in...' : 'Login to Dashboard'}
          </button>

          <div style={{ textAlign: 'center' }}>
            <button
              type="button"
              onClick={onSwitchToWaiter}
              style={{
                background: 'none',
                border: '2px solid #FF6B00',
                color: '#FF6B00',
                borderRadius: '25px',
                padding: '8px 20px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '500',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.background = '#FF6B00';
                e.target.style.color = 'white';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'none';
                e.target.style.color = '#FF6B00';
              }}
            >
              Login as Waiter
            </button>
          </div>
        </form>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', padding: '30px',
            maxWidth: '420px', width: '90%',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            animation: 'fadeIn 0.2s ease'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '1.4rem', fontWeight: '700' }}>
              Reset Password
            </h3>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '0.9rem', lineHeight: '1.5' }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>

            {forgotMessage && (
              <div style={{
                background: forgotMessage.startsWith('success:') ? '#d4edda' : '#fee2e2',
                border: '1px solid',
                borderColor: forgotMessage.startsWith('success:') ? '#c3e6cb' : '#fecaca',
                borderRadius: '8px', padding: '12px', marginBottom: '15px',
                color: forgotMessage.startsWith('success:') ? '#155724' : '#dc2626',
                fontSize: '0.9rem'
              }}>
                {forgotMessage.replace(/^(success:|error:)/, '')}
              </div>
            )}

            <input
              type="email"
              placeholder="Email Address"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleForgotPassword()}
              style={{
                width: '100%', padding: '12px 15px', border: '2px solid #e5e7eb',
                borderRadius: '10px', fontSize: '1rem', marginBottom: '20px',
                outline: 'none', transition: 'border-color 0.3s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#FF6B00'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setForgotMessage('');
                }}
                style={{
                  padding: '10px 20px', border: '2px solid #6c757d',
                  backgroundColor: 'transparent', color: '#6c757d',
                  borderRadius: '10px', cursor: 'pointer', fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleForgotPassword}
                disabled={forgotLoading}
                style={{
                  padding: '10px 24px',
                  background: forgotLoading ? '#ccc' : 'linear-gradient(135deg, #FF4444, #FF6B00)',
                  color: 'white', border: 'none',
                  borderRadius: '10px', cursor: forgotLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600', transition: 'all 0.3s ease',
                  boxShadow: forgotLoading ? 'none' : '0 4px 12px rgba(255, 68, 68, 0.3)'
                }}
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ManagerLogin;
